<template>
    <div v-dark class="study-progress">
        <ResetProgressModal
            v-if="showResetProgressModal"
            :is-loading="isLoading"
            @resetProgress="resetProgress"
            @close="showResetProgressModal = false"
        />
        <div v-dark class="study-progress__head">
            <div v-dark class="study-progress__title">
                <Icon type="questions" />
                <h2>{{ hasSubscription ? '' : 'Free ' }}Study Progress</h2>
                <span v-dark class="study-progress__title-dot">&bull;</span>
                <span v-dark class="study-progress__progress-percent">{{ progressPercent }}%</span>
            </div>
            <PocketLink
                v-if="bestAnswers.length"
                :is-dark-mode="isDarkMode"
                @click="showResetProgressModal = true"
            >
                Reset Progress
            </PocketLink>
        </div>
        <div class="study-progress__content">
            <div v-dark class="study-progress__progress-bar">
                <div
                    v-dark
                    class="study-progress__progress-bar-fill"
                    :class="{ 'study-progress__progress-bar-fill--100': isComplete }"
                    :style="{ width: `${progressPercent}%`}"
                />
            </div>
            <div class="study-progress__receipt">
                <div class="study-progress__receipt-item">
                    <div v-dark class="study-progress__receipt-label">
                        {{ hasSubscription ? 'Premium' : 'Free' }} Question Bank
                    </div>
                    <div v-dark class="study-progress__receipt-value">{{ totalQuestionCount }}</div>
                </div>
                <div class="study-progress__receipt-item">
                    <div v-dark class="study-progress__receipt-label">Answered Questions</div>
                    <div v-dark class="study-progress__receipt-value">{{ bestAnswers.length }}</div>
                </div>
                <div class="study-progress__receipt-item">
                    <div v-dark class="study-progress__receipt-label">Unanswered Questions</div>
                    <div v-dark class="study-progress__receipt-value">{{ unansweredCount }}</div>
                </div>
            </div>
            <div v-if="answeredPremiumCount"
                 v-dark
                 class="study-progress__locked">
                <Icon v-dark
                      type="lock"
                      class="study-progress__locked-icon"
                />
                <div class="study-progress__receipt-item study-progress__locked-item">
                    <div v-dark class="study-progress__receipt-label">Answered Premium Questions</div>
                    <div v-dark class="study-progress__receipt-value">{{ answeredPremiumCount }}</div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { examMetadataModule } from '@/store/examMetadata/module'
import { quizModule } from '@/store/quiz/module'
import { subscriptionModule } from '@/store/subscription/module'
import { userModule } from '@/store/user/module'
import UIKit from '@pocketprep/ui-kit'
import ResetProgressModal from '@/components/Stats/ResetProgressModal.vue'
import { userExamMetadataModule } from '@/store/userExamMetadata/module'

const {
    Icon,
    Link: PocketLink,
} = UIKit

const showResetProgressModal = ref(false)
const isLoading = ref(false)

const isDarkMode = computed(() => {
    return userModule.state.settings.isDarkMode
})

const hasSubscription = computed(() => {
    return subscriptionModule.getters.getSubscriptionForExamId()
})

const bestAnswers = computed(() => {
    return Object.values(
        quizModule.getters.getLatestAnswers({ questionFilter: hasSubscription.value ? 'all' : 'free' })
    )
})

const answeredPremiumCount = computed(() => {
    if (hasSubscription.value) {
        return 0
    }

    const allCount = Object.keys(quizModule.getters.getLatestAnswers({ questionFilter: 'all' })).length
    return Math.max(allCount - bestAnswers.value.length, 0)
})

const examMetadata = computed(() => {
    return examMetadataModule.getters.getCurrentExamMetadata()
})

const totalQuestionCount = computed(() => {
    if (hasSubscription.value) {
        return ((examMetadata.value?.itemCount || 0) - (examMetadata.value?.archivedCount || 0))
    }

    return examMetadata.value?.specialQuestions || 0
})

const progressPercent = computed(() => {
    return Math.round(((bestAnswers.value.length / totalQuestionCount.value) || 0) * 100)
})

const isComplete = computed(() => {
    return totalQuestionCount.value > 0 && bestAnswers.value.length === totalQuestionCount.value
})

const unansweredCount = computed(() => {
    return Math.max(totalQuestionCount.value - bestAnswers.value.length, 0)
})

onMounted(() => {
    examMetadataModule.actions.fetchExamMetadata()
    userModule.actions.fetchUserData()
})

const resetProgress = async () => {
    if (!isLoading.value && examMetadata.value) {
        isLoading.value = true
        await userExamMetadataModule.actions.resetExamProgress(examMetadata.value)
        showResetProgressModal.value = false
        isLoading.value = false
    }
}
</script>

<style lang="scss" scoped>
.study-progress {
    background: $white;
    border: 1px solid $gray-divider;
    border-radius: 6px;
    padding: 11px 14px 14px;
    box-sizing: border-box;
    position: relative;

    &--dark {
        background: $brand-black;
        border-color: $slate;
        color: $fog;
    }

    &__head {
        display: flex;
        justify-content: space-between;
        align-items: center;
        box-sizing: border-box;
        height: 32px;
        padding-bottom: 7px;
        border-bottom: 2px solid $gray-divider;
        padding-left: 8px;

        &--dark {
            padding-bottom: 8px;
            border-bottom-width: 2px;
            border-color: $ash;
        }
    }

    &__title {
        display: flex;
        align-items: center;

        svg {
            height: 22px;
            margin-right: 8px;
            color: $slate;
        }

        &--dark {
            color: $white;
        }

        &--dark svg {
            color: $fog;
        }

        h2 {
            margin: 0;
            font-size: 16px;
            line-height: 22px;
            font-weight: 600;
        }
    }

    &__title-dot {
        margin: 0 8px;
        font-size: 11px;
        font-weight: 600;

        &--dark {
            color: $white;
        }
    }

    &__progress-percent {
        font-size: 16px;
        line-height: 22px;
        font-weight: 600;

        &--dark {
            color: $white;
        }
    }

    &__content {
        padding: 16px 0 0;
    }

    &__progress-bar {
        border-radius: 2px;
        background: $pearl;
        height: 12px;
        position: relative;
        border: 1px solid rgba($pewter, 0.85);

        &--dark {
            background: $jet;
            border-color: $slate;
        }
    }

    &__progress-bar-fill {
        background: $brand-black;
        height: 14px;
        position: absolute;
        top: -1px;
        left: -1px;
        border-radius: 2px;

        &--dark {
            background: $barely-background;
        }

        &--100 {
            background: url('@/assets/stats/progress-easter-egg.svg') no-repeat center center;
            background-size: cover;
            width: calc(100% + 2px) !important;
        }
    }

    &__receipt {
        margin-top: 16px;
    }

    &__receipt-item {
        display: flex;
        justify-content: space-between;
        align-items: center;
        height: 20px;
        margin-bottom: 7px;
        font-size: 15px;
        line-height: 20px;

        &:last-child {
            margin-bottom: 0;
        }
    }

    &__locked {
        position: relative;
        margin-top: 12px;
        padding-top: 12px;
        border-top: 1px solid $fog;

        &--dark {
            border-color: $ash;
        }
    }

    &__locked-item {
        padding-left: 24px;
    }

    &__locked-icon {
        position: absolute;
        left: -3px;
        top: 12px;
        width: 21px;
        height: 22px;
        color: $slate;

        &--dark {
            color: $fog;
        }
    }

    &__receipt-label {
        color: $moonlit-ocean;

        &--dark {
            color: $fog;
        }
    }

    &__receipt-value {
        font-size: 17px;
        letter-spacing: -0.1px;
        font-weight: 600;
        color: $brand-black;

        &--dark {
            color: $white;
        }
    }
}
</style>
